/**
 * PlaySidebar — Fixed sidebar for the Play page.
 *
 * Renders three modes based on planningStep:
 *   'choose'    → Activity selection panel + equipment
 *   'move'      → Movement budget bar + "Lock In" / "Back" buttons
 *   'narrative'  → NarrativePanel via children slot
 */

import { Heart, Shield, Footprints, ArrowLeft, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ActivityPanel } from '@/components/game-ui/stretch/ActivityPanel';
import { QuestTracker } from '@/components/game-ui/QuestTracker';
import type { Quest } from '@/lib/game-logic/templates';
import type { StretchActivity, StretchResolution, StretchPhase } from '@omen-quest/stretch-engine';

// ============================================================================
// TYPES
// ============================================================================

type PlanningStep = 'choose' | 'move' | 'narrative';

export interface PlaySidebarProps {
  character: {
    name: string;
    vitals: number;
    vitalsMax: number;
    resolve: number;
    resolveMax: number;
    stats: { brawn: number; grace: number; wits: number; spirit: number };
    equipment: string[];
  };
  stretchNumber: number;
  stretchPhase: StretchPhase;
  planningStep: PlanningStep;
  moveCount: number;
  movementBudget: number;
  selectedActivity: StretchActivity | null;
  onSelectActivity: (activity: StretchActivity) => void;
  onConfirmActivity: () => void;
  onBackToChoose: () => void;
  onCommit: () => void;
  lastResolution: StretchResolution | null;
  quest?: Quest;
  triggerFlags?: Set<string>;
  disabled?: boolean;
  children?: React.ReactNode;
}

// ============================================================================
// HELPERS
// ============================================================================

const pct = (current: number, max: number) =>
  max > 0 ? (current / max) * 100 : 0;

function getVitalsColor(ratio: number): string {
  if (ratio > 0.6) return 'bg-emerald-500';
  if (ratio > 0.3) return 'bg-yellow-500';
  return 'bg-red-500';
}

const STEP_LABELS: Record<PlanningStep, string> = {
  choose: 'Choose Activity',
  move: 'Move & Commit',
  narrative: 'Narrative',
};

const STEP_BADGE_CLASS: Record<PlanningStep, string> = {
  choose: 'bg-amber-500/15 text-amber-400',
  move: 'bg-blue-500/15 text-blue-400',
  narrative: 'bg-amber-500/15 text-amber-400',
};

// ============================================================================
// COMPONENT
// ============================================================================

export function PlaySidebar({
  character,
  stretchNumber,
  stretchPhase,
  planningStep,
  moveCount,
  movementBudget,
  selectedActivity,
  onSelectActivity,
  onConfirmActivity,
  onBackToChoose,
  onCommit,
  lastResolution,
  quest,
  triggerFlags,
  disabled = false,
  children,
}: PlaySidebarProps) {
  const vitalsRatio = character.vitalsMax > 0 ? character.vitals / character.vitalsMax : 0;
  const movesRemaining = Math.max(0, movementBudget - moveCount);

  return (
    <div className="h-full flex flex-col border-l border-white/10 bg-black">
      {/* ── Character Mini Strip ── */}
      <div className="px-4 py-3 border-b border-white/10 shrink-0">
        <h2 className="text-sm font-semibold text-white mb-2">{character.name}</h2>

        {/* Vitals bar */}
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <Heart className="w-3.5 h-3.5 text-red-400 shrink-0" />
            <div className="flex-1 h-2 bg-neutral-800 rounded-full overflow-hidden">
              <div
                className={cn('h-full rounded-full transition-all', getVitalsColor(vitalsRatio))}
                style={{ width: `${pct(character.vitals, character.vitalsMax)}%` }}
              />
            </div>
            <span className="text-xs text-neutral-400 tabular-nums w-12 text-right">
              {character.vitals}/{character.vitalsMax}
            </span>
          </div>

          {/* Resolve bar */}
          <div className="flex items-center gap-2">
            <Shield className="w-3.5 h-3.5 text-blue-400 shrink-0" />
            <div className="flex-1 h-2 bg-neutral-800 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all bg-blue-500"
                style={{ width: `${pct(character.resolve, character.resolveMax)}%` }}
              />
            </div>
            <span className="text-xs text-neutral-400 tabular-nums w-12 text-right">
              {character.resolve}/{character.resolveMax}
            </span>
          </div>
        </div>

        {/* Stats row */}
        <div className="flex gap-3 mt-2">
          {Object.entries(character.stats).map(([stat, val]) => (
            <div key={stat} className="text-center">
              <div className="text-[10px] uppercase tracking-wide text-neutral-500">
                {stat.slice(0, 3)}
              </div>
              <div className="text-xs font-medium text-neutral-300">{val}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Quest Tracker ── */}
      {quest && (
        <QuestTracker quest={quest} completedFlags={triggerFlags ?? new Set()} />
      )}

      {/* ── Stretch Info ── */}
      <div className="px-4 py-2.5 border-b border-white/10 shrink-0">
        <div className="flex items-center justify-between">
          <span className="text-xs text-neutral-400">
            Stretch #{stretchNumber}
          </span>
          <span className={cn(
            'text-xs font-medium px-2 py-0.5 rounded-full',
            STEP_BADGE_CLASS[planningStep],
          )}>
            {STEP_LABELS[planningStep]}
          </span>
        </div>
      </div>

      {/* ── Step: Narrative → children (NarrativePanel) ── */}
      {planningStep === 'narrative' && children ? (
        <div className="flex-1 min-h-0 flex flex-col">
          {children}
        </div>
      ) : planningStep === 'move' ? (
        /* ── Step: Move & Commit ── */
        <div className="flex-1 overflow-y-auto">
          {/* Movement budget */}
          <div className="px-4 py-2.5 border-b border-white/10">
            <div className="flex items-center gap-2">
              <Footprints className="w-3.5 h-3.5 text-blue-400" />
              <span className="text-xs text-neutral-400">
                {movesRemaining}/{movementBudget} tiles remaining
              </span>
            </div>
            <div className="mt-1.5 h-1.5 bg-neutral-800 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-blue-500/70 transition-all"
                style={{ width: `${pct(movesRemaining, movementBudget)}%` }}
              />
            </div>
          </div>

          {/* Selected activity reminder */}
          {selectedActivity && (
            <div className="px-4 py-2.5 border-b border-white/10">
              <div className="text-[10px] uppercase tracking-wide text-neutral-500 mb-1">
                Activity
              </div>
              <div className="text-xs text-neutral-300">
                {selectedActivity.type.replace(/_/g, ' ')}
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="px-4 py-3 space-y-2">
            <p className="text-xs text-neutral-500 leading-relaxed mb-3">
              Move around using WASD or clicking adjacent tiles, then lock in to resolve this stretch.
            </p>
            <button
              onClick={onCommit}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-emerald-600/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-600/30 transition-colors"
            >
              <Lock className="w-4 h-4" />
              Lock In
            </button>
            <button
              onClick={onBackToChoose}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs text-neutral-500 hover:text-neutral-300 hover:bg-white/5 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to Activity
            </button>
          </div>

          {/* Resolution log (from previous stretch) */}
          {lastResolution && (
            <div className="px-4 py-3 border-t border-white/10">
              <h3 className="text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-2">
                Last Stretch Results
              </h3>
              <ResolutionLog resolution={lastResolution} />
            </div>
          )}

          {/* Equipment */}
          <EquipmentList equipment={character.equipment} />
        </div>
      ) : (
        /* ── Step: Choose Activity ── */
        <div className="flex-1 overflow-y-auto">
          {/* Activity Panel */}
          {stretchPhase === 'planning' && (
            <div className="px-4 py-3 border-b border-white/10">
              <ActivityPanel
                selectedActivity={selectedActivity}
                onSelectActivity={onSelectActivity}
                onConfirm={onConfirmActivity}
                disabled={disabled}
              />
            </div>
          )}

          {/* Resolution log (from previous stretch) */}
          {lastResolution && (
            <div className="px-4 py-3 border-b border-white/10">
              <h3 className="text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-2">
                Last Stretch Results
              </h3>
              <ResolutionLog resolution={lastResolution} />
            </div>
          )}

          {/* Equipment */}
          <EquipmentList equipment={character.equipment} />
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SUB-COMPONENTS (extracted to avoid duplication between steps)
// ============================================================================

function ResolutionLog({ resolution }: { resolution: StretchResolution }) {
  return (
    <div className="space-y-1">
      {resolution.activityResults.map((ar, i) => (
        <div key={i} className="text-xs text-neutral-300">
          <span className="text-neutral-500">{ar.activityType}:</span>{' '}
          {ar.tilesRevealed > 0 && (
            <span>Revealed {ar.tilesRevealed} tiles. </span>
          )}
          {ar.itemsFound.length > 0 && (
            <span>Found: {ar.itemsFound.join(', ')}. </span>
          )}
          {ar.notes.map((note, j) => (
            <span key={j}>{note} </span>
          ))}
        </div>
      ))}

      {resolution.wanderingMonster.checked && (
        <div className={cn(
          'text-xs',
          resolution.wanderingMonster.triggered
            ? 'text-red-400'
            : 'text-neutral-500',
        )}>
          {resolution.wanderingMonster.triggered
            ? `Monster: ${resolution.wanderingMonster.encounter?.description ?? 'Unknown'}`
            : `Monster check: safe (${resolution.wanderingMonster.rolled})`
          }
        </div>
      )}

      {resolution.lightSourcesDepleted.length > 0 && (
        <div className="text-xs text-yellow-400">
          Light depleted: {resolution.lightSourcesDepleted.map(ls => ls.type).join(', ')}
        </div>
      )}
    </div>
  );
}

function EquipmentList({ equipment }: { equipment: string[] }) {
  return (
    <div className="px-4 py-3">
      <h3 className="text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-2">
        Equipment
      </h3>
      <ul className="space-y-0.5">
        {equipment.map((item, i) => (
          <li key={i} className="text-xs text-neutral-300">
            {item}
          </li>
        ))}
        {equipment.length === 0 && (
          <li className="text-xs text-neutral-600 italic">No equipment</li>
        )}
      </ul>
    </div>
  );
}
